import { FC, useState, useCallback, useMemo } from 'react';
import { ImageProps } from 'next/image';
import { getCDNDrugImageURL } from 'src/utils/cdnStaticSourcesHelper';
import dynamic from 'next/dynamic';
import Skeleton from 'src/shared-components/Loaders/Skeleton';
import LoadingShimmer from './LoadingShimmer';

const errorImageSrc = 'drug/robot-with-star.svg';

const NextImage = dynamic(() => import('../NextImage/NextImage'), {
  loading: () => <Skeleton width={'100%'} height={32} />
});

interface Props extends Partial<ImageProps> {
  drugId: string;
  size: 'S' | 'M' | 'L';
  drugName: string;
  isPriorityLoading?: boolean;
  imageExists: boolean;
}

const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str);

const DrugImage: FC<Props> = ({
  drugId,
  drugName,
  isPriorityLoading,
  size,
  width,
  height,
  imageExists,
  ...props
}) => {
  const [isPngTried, setIsPngTried] = useState(false);
  const handleOnError = useCallback(() => {
    setIsPngTried(true);
  }, [isPngTried]);

  const imageSrcProps = useMemo(() => {
    if (imageExists) {
      if (isPngTried) {
        const imgPngSrc = getCDNDrugImageURL(drugId, size, 'png');
        return {
          loader: () => imgPngSrc,
          src: imgPngSrc,
          pureSrc: true
        };
      }

      const imgWebpSrc = getCDNDrugImageURL(drugId, size);

      return {
        loader: () => imgWebpSrc,
        src: imgWebpSrc,
        pureSrc: true
      };
    }

    return {
      src: errorImageSrc
    };
  }, [drugId, size, isPngTried]);

  return (
    <NextImage
      alt={drugName}
      title={drugName}
      width={width}
      height={height}
      onError={handleOnError}
      placeholder="blur"
      blurDataURL={`data:image/svg+xml;base64,${toBase64(
        LoadingShimmer(width, height)
      )}`}
      priority={isPriorityLoading}
      {...props}
      {...imageSrcProps}
    />
  );
};

export default DrugImage;
